import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { connectSocket } from "../socket";
import SideBar from "./SideBar";
import InteractiveAvatar from "./HeyGen/InteractiveAvatar";
import Speedometer from "./Speedometer";
import { FileText } from "lucide-react";

// Add this mapping near the top of the file (after imports or at the start of the component):
const categoryDisplayNames = {
  'defects': 'Defects',
  'Product docs': 'Product Docs',
  'case notes': 'Case Notes'
};

// Add category color mapping
const getCategoryColor = (category) => {
  const categoryLower = category.toLowerCase();
  
  if (categoryLower === 'defects') {    return {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-200',
      hover: 'hover:bg-blue-200',
      iconColor: 'text-blue-600'
    };
  } else if (categoryLower === 'case notes') {    return {
      bg: 'bg-purple-100',
      text: 'text-purple-800',
      border: 'border-purple-200',
      hover: 'hover:bg-purple-200',
      iconColor: 'text-purple-600'
    };
  } else if (categoryLower === 'product docs') {    return {
      bg: 'bg-yellow-100',
      text: 'text-yellow-800',
      border: 'border-yellow-200',
      hover: 'hover:bg-yellow-200',
      iconColor: 'text-yellow-600'
    };
  }
  
  // Default fallback
  return {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    border: 'border-gray-200',
    hover: 'hover:bg-gray-200',
    iconColor: 'text-gray-600'
  };
};

// Function to extract category from document name
const getCategoryFromDocumentName = (documentName) => {
  if (!documentName) return "Product Documents";
  
  const nameLower = documentName.toLowerCase();
  
  // Check for defect-related keywords FIRST (more specific)
  if (nameLower.includes('defect') || nameLower.includes('csc') || nameLower.includes('bug')) {
    return 'defects';
  }
  
  // Check for case-related keywords (but not if it contains csc)
  if ((nameLower.includes('case') || nameLower.includes('cs')) && !nameLower.includes('csc')) {
    return 'case notes';
  }
  
  // Check for product docs keywords
  if (nameLower.includes('product') || nameLower.includes('doc') || nameLower.includes('guide') || nameLower.includes('manual')) {
    return 'product docs';
  }
  
  // Default to product docs
  return 'product docs';
};

const ChatWindow = ({ username, onLogout }) => {
  const navigate = useNavigate();
  const [socket, setSocket] = useState(null);
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState("diagnostic");
  const [searchSubMode, setSearchSubMode] = useState("perplexity");
  const [diagnosticMessages, setDiagnosticMessages] = useState([]);
  const [searchMessages, setSearchMessages] = useState([]);
  const [copilotMessages, setCopilotMessages] = useState([]); // Add copilot messages state
  const [documentSummaries, setDocumentSummaries] = useState({}); // Store AI summaries by document path
  // Next two lines deleted by an incorrect merge are restored now.
  const [pastedImage, setPastedImage] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false); // Store pasted image data
  // Computed messages after mode is initialized
  const messages = mode === "diagnostic" ? diagnosticMessages : mode === "search" ? searchMessages : copilotMessages;
  const setMessages = mode === "diagnostic" ? setDiagnosticMessages : mode === "search" ? setSearchMessages : setCopilotMessages;
  
  // Update modeRef when mode changes and add debug logging
  useEffect(() => {
    modeRef.current = mode; // Keep ref in sync
    console.log("🔄 Mode changed to:", mode);
    console.log("🔄 Messages count:", messages.length);
    console.log("🔄 Diagnostic:", diagnosticMessages.length, "Search:", searchMessages.length, "Copilot:", copilotMessages.length);
  }, [mode, messages.length, diagnosticMessages.length, searchMessages.length, copilotMessages.length]);

  const [isWaitingForCommand, setIsWaitingForCommand] = useState(false);
  const [isWaitingForDecision, setIsWaitingForDecision] = useState(false);
  const [inRecommendation, setInRecommendation] = useState(false);
  const [recommendationStartIdx, setRecommendationStartIdx] = useState(null);
  const chatBodyRef = useRef(null);
  const modeRef = useRef(mode); // Track current mode for socket handlers

  const [currentSearchId, setCurrentSearchId] = useState(null); // Tracks current search
  const [searchSourcesMap, setSearchSourcesMap] = useState({}); // Stores sources per search
  const [generatedOverviews, setGeneratedOverviews] = useState(new Set()); // Track which documents have generated overviews
  const [queryOverviews, setQueryOverviews] = useState({}); // Store overviews per query

  // Simple typing indicator for diagnostic mode
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  // Add escalation state
  const [escalationActive, setEscalationActive] = useState(false);

  // Function to get current agent name
  const getCurrentAgentName = () => {
    const bot = participants.find(p => p.role === "bot");
    if (bot && bot.name) {
      return bot.name;
    }
    // Fallback names based on session type or default
    return "AI Agent";
  };

  // Image Component
  const [zoomImageUrl, setZoomImageUrl] = useState(null);
  const handleImageClick = (url) => setZoomImageUrl(url);
  const closeZoom = () => setZoomImageUrl(null);

  // Video Chat State
  const [videoOpen, setVideoOpen] = useState(false);
  const videoRef = useRef(null);

  // HeyGen Avatar State
  const [avatarActive, setAvatarActive] = useState(false); // Avatar session active
  const [avatarPanelOpen, setAvatarPanelOpen] = useState(false); // Panel visibility
  const [avatarStatus, setAvatarStatus] = useState("idle");
  const avatarRef = useRef(null);

  // Debug avatarActive changes
  useEffect(() => {
    console.log("🎭 avatarActive changed to:", avatarActive);
    console.trace("Stack trace for avatarActive change:");
  }, [avatarActive]);

  // Debug avatarPanelOpen changes
  useEffect(() => {
    console.log("🎭 avatarPanelOpen changed to:", avatarPanelOpen);
  }, [avatarPanelOpen]);

  // Dynamic participants for avatars
  const [participants, setParticipants] = useState([]);
  const [botAvatarId, setBotAvatarId] = useState(null); // Track bot's avatar ID

  //const [escalationActive, setEscalationActive] = useState(false);
  const [agentNotification, setAgentNotification] = useState("");

  const [sentiment, setSentiment] = useState(10);
  const [sessionStatus, setSessionStatus] = useState({ hasActiveSession: false });
  const [lastDiagnosticState, setLastDiagnosticState] = useState(null);

  // const [sources, setSources] = useState([
  //   {
  //     title: "A Guide to Network Troubleshooting",
  //     url: "https://www.comptia.org/content/guides/a-guide-to-network-troubleshooting",
  //     favicon: "https://www.comptia.org/favicon.ico"
  //   },
  //   {
  //     title: "Network Troubleshooting - Methodology, Steps &...",
  //     url: "https://www.softwaretestinghelp.com/network-troubleshooting/",
  //     favicon: "https://www.softwaretestinghelp.com/favicon.ico"
  //   },
  //   {
  //     title: "Network Troubleshooting: Documentation - Dummies",
  //     url: "https://www.dummies.com/article/technology/information-technology/networking/network-troubleshooting-documentation-207865/",
  //     favicon: "https://www.dummies.com/favicon.ico"
  //   }
  // ]);

  const [diagnosticSources, setDiagnosticSources] = useState([]);
  const [ragSources, setRagSources] = useState([]);

  // Sources panel state
  const [sourcesPanelOpen, setSourcesPanelOpen] = useState(false);
  const [userHasSentMessage, setUserHasSentMessage] = useState(false);
  const [sourcesVisible, setSourcesVisible] = useState(false);
  const [visibleSourceCount, setVisibleSourceCount] = useState(0);

  // Add new state for sidebar mode
  const [sidebarMode, setSidebarMode] = useState("sources"); // "sources" or "search"
  const [searchQuery, setSearchQuery] = useState("");

  // Add support type state with a welcome message
  const [supportType, setSupportType] = useState("WELCOME TO ARTA AI SUPPORT");
  const [displayTitle, setDisplayTitle] = useState("WELCOME TO ARTA AI SUPPORT");
  const [isTyping, setIsTyping] = useState(false);
  const [isTitleSet, setIsTitleSet] = useState(false);

  // Typewriter effect hook
  useEffect(() => {
    if (supportType === displayTitle) return;
    
    setIsTyping(true);
    let currentIndex = 0;
    const targetText = supportType;
    
    const typingInterval = setInterval(() => {
      if (currentIndex <= targetText.length) {
        setDisplayTitle(targetText.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setIsTyping(false);
      }
    }, 50); // Adjust speed here (lower = faster)
    
    return () => clearInterval(typingInterval);
  }, [supportType]);

  // Add function to get title from message
  const getTitleFromMessage = (message) => {
    const msg = message.toLowerCase();
    if (msg.includes("network")) return "NETWORK ISSUE";
    if (msg.includes("server")) return "SERVER ISSUE";
    if (msg.includes("graphiant")) return "GRAPHIANT ISSUE";
    return "WELCOME TO ARTA AI SUPPORT"; // Default title when no keywords found
  };

  // Auto-open sources panel when sources are available
  useEffect(() => {
    if (diagnosticSources.length > 0) {
      setSidebarMode("sources"); // Set to sources mode (not search mode)
      setSourcesPanelOpen(true);
      setSourcesVisible(true);
      setVisibleSourceCount(0);
    }
  }, [diagnosticSources.length]);

  // Keep the animation effect for sources when they become visible
  useEffect(() => {
    if (sourcesVisible && visibleSourceCount < diagnosticSources.length) {
      const timeout = setTimeout(() => {
        setVisibleSourceCount((count) => count + 1);
      }, 250);
      return () => clearTimeout(timeout);
    }
  }, [sourcesVisible, visibleSourceCount, diagnosticSources.length]);

  useEffect(() => {
    const s = connectSocket();
    setSocket(s);

    s.on("connect", () => {
      console.log("✅ Socket connected:", s.id);
      // Join a room with session ID and role
      const sessionId = s.id;
      console.log("calling join room from connect")
      s.emit("joinRoom", { sessionId, role: "user", name: username || "User" });
      
      // Join copilot room as user
      console.log("🔌 Joining copilot room as user...");
      s.emit("joinCopilotRoom", {
        room_id: "copilot_room",
        role: "user",
        name: username || "User"
      });
      
      // Request session status on connect
      setTimeout(() => {
        s.emit("getSessionStatus");
      }, 500); // Small delay to ensure session is set up
    });

    s.on("agentJoined", () => {
      console.log("Agent Joined Escalation - A Live agent has joined the chat");
      setEscalationActive(true); // Block typing indicator after escalation
      setShowTypingIndicator(false); // Immediately hide typing indicator
      setAgentNotification("A live agent has joined the chat.");
      setTimeout(() => setAgentNotification(""), 5000);
    });

    s.on("escalationAlert", () => {
      setEscalationActive(true);
      setShowTypingIndicator(false);
    });

    s.on("chatMessage", (msg) => {
      const { text, response, done, sender, queryType } = msg;
      // Update support type if queryType is received and title hasn't been set yet
      if (queryType && !isTitleSet && queryType !== "network") {
        setSupportType(queryType);
        console.log("📋 Support type updated to:", queryType);
      }
      
      const displayText = text || response || "🤖 AI Agent: [No response]";
      console.log("📨 Received chatMessage:", { text, response, sender, timestamp: msg.timestamp, queryType });
      
      // Hide typing indicator and add message for bot responses in diagnostic mode
      if (mode === "diagnostic" && (sender === "bot" || !sender)) {
        setShowTypingIndicator(false);
        setDiagnosticMessages((prev) => [...prev, { 
          text: displayText, 
          sender: sender || "bot", 
          timestamp: msg.timestamp 
        }]);
      } else {
        // For user messages or search mode, add immediately
        setDiagnosticMessages((prev) => [...prev, { 
          text: displayText, 
          sender: sender || "bot", 
          timestamp: msg.timestamp 
        }]);
      }

      // SIMPLE APPROACH: If there's an avatar ref and this is a bot message, send it directly
      if (avatarRef.current && (sender === "bot" || !sender) &&
      !displayText.includes("Troubleshooting Image")
      ) {
        console.log("🎤 Direct sending to avatar:", displayText);
        
        avatarRef.current.sendMessage(displayText);
      }
      
      if (
        displayText.includes("Please execute the following command") ||
        displayText.includes("Using the below command's output")
      )
        setIsWaitingForCommand(true);

      if (
        displayText.includes("Would you like to proceed") ||
        displayText.includes("Shall I close this ticket")
      )
        setIsWaitingForDecision(true);

      if (done) {
        setIsWaitingForCommand(false);
        setIsWaitingForDecision(false);
      }
    });

    // Handle full conversation history (for agent join)
    s.on("conversationHistory", (historyArr) => {
      // historyArr: [{text, sender}, ...]
      setMessages(historyArr);
    });

    // Handle liveAgentMessage (TSE agent messages - only for copilot mode)
    s.on("liveAgentMessage", (msg) => {
      // msg: {text, sender, agentName, timestamp}
      console.log("👨‍💼 Received liveAgentMessage:", msg);
      console.log("👨‍💼 Current mode (ref):", modeRef.current);
      
      const messageData = {
        text: msg.text,
        sender: msg.sender || "agent",
        agentName: msg.agentName,
        timestamp: msg.timestamp,
      };
      
      if (modeRef.current === "copilot") {
        console.log("👨‍💼 Adding to copilot messages");
        setCopilotMessages((prev) => {
          console.log("👨‍💼 Previous copilot messages:", prev.length);
          const updated = [...prev, messageData];
          console.log("👨‍💼 Updated copilot messages:", updated.length);
          return updated;
        });
      } else {
        console.log("👨‍💼 Not in copilot mode, ignoring message");
        // TSE agent messages should only be handled in copilot mode
        // In diagnostic mode, these messages should be ignored
      }
    });

    // Handle TSE agent joining notification
    s.on("tseAgentJoined", (data) => {
      console.log("👨‍💼 TSE Agent joined:", data);
      // This is the only place where we can extract TSECopilot Live Agent details, since it is not passed as participants or copilot_participants list
      let tseAgentPresent = "true";
      localStorage.setItem('IsTseAgentPresent', tseAgentPresent);
      localStorage.setItem('TseAgentName', data.agentName);
      if (modeRef.current === "copilot") {
        // Add system message that TSE agent has joined
        const systemMessage = {
          // Remove hardcoded Agent Name Chris
          text: `🎉 `+ capitalize(data.agentName) + ` has joined your session and is ready to assist you!`,
          sender: "system",
          timestamp: Date.now(),
        };
        setCopilotMessages((prev) => [...prev, systemMessage]);
      }
    });

    // Listen for dynamic room participants
    s.on("roomParticipants", (list) => {
      setParticipants(list);
      // Extract bot's avatar_id if available
      const bot = list.find(p => p.role === "bot");
      if (bot && bot.avatar_id) {
        setBotAvatarId(bot.avatar_id);
        console.log("🎭 Bot avatar ID set to:", bot.avatar_id);
      }
    });

    s.on("sentimentUpdate", (data) => {
      console.log("[Frontend] Received sentimentUpdate:", data);
      if (data && typeof data.sentiment === "number") {
        setSentiment(data.sentiment);
      }
    });

    // Listen for search responses (RAG system responses)
    // s.on("searchResponse", (msg) => {
    //   const { text, sender, timestamp, query, rag_sources } = msg;
    //   console.log("📨 Received searchResponse:", { text, sender, timestamp, query });
      
    //   const displayText = text || "🤖 Search: [No response]";
      


    //   // Format sources (if any)
    //   const formattedSources = (rag_sources || []).map((item) => {
    //     const relativePath = typeof item === "string" ? item : item.link;
    //     const fullUrl = `https://api.artasupport.com/static/images/${relativePath}`;
    //     return {
    //       link: fullUrl || "No URL found.",
    //       name: item.name || "No File name found.",
    //       summary: item.summary || "No Summary available.",
    //       icon: "pdf",
    //     };
    //   });
    //   // Only update search messages for searchResponse events
    //   setSearchMessages((prev) => [...prev, { 
    //     text: displayText, 
    //     sender: sender || "bot", 
    //     timestamp: timestamp,
    //     isSearchResponse: true,
    //     sources: formattedSources || [] // Add sources field here
 
    //   }]);

    // });
    s.on("searchResponse", (msg) => {
      const { text, sender, timestamp, query, rag_sources, complete } = msg;

      setSearchMessages((prev) => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;

        // Case 1: Update existing streaming message
        if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
          const lastMsg = updated[lastIdx];

          // Use replace instead of += for controlled updates
          const newText = complete ? lastMsg.text : lastMsg.text + text;

          updated[lastIdx] = {
            ...lastMsg,
            text: newText,
            isStreaming: !complete,
            timestamp: complete ? timestamp : lastMsg.timestamp,
            sources: complete
              ? (rag_sources || []).map((item) => {
                  const relativePath = typeof item === "string" ? item : item.link;
                  // const fullUrl = `https://staging-api.artasupport.com/static/images/${relativePath}`;
                  const fullUrl = `https://api.artasupport.com/static/images/${relativePath}`;
                  return {
                    link: fullUrl,
                    name: item.name || "No File name found.",
                    summary: item.summary || "No Summary available.",
                    category: item.category || "Product Documents",
                    icon: "pdf",
                  };
                })
              : lastMsg.sources,
          };

          return updated;
        }

        // Case 2: No existing streaming message, so add new one
        return [
          ...prev,
          {
            text,
            sender,
            timestamp,
            isSearchResponse: true,
            isStreaming: !complete,
            sources: (rag_sources || []).map((item) => {
              const relativePath = typeof item === "string" ? item : item.link;
              const fullUrl = `https://staging-api.artasupport.com/static/images/${relativePath}`;
              return {
                link: fullUrl,
                name: item.name || "No File name found.",
                summary: item.summary || "No Summary available.",
                category: item.category || "Product Documents",
                icon: "pdf",
              };
            }),
          },
        ];
      });
    });

    // Listen for diagnostic sources

    s.on("diagnosticSources", (msg) => {
    const { sources = [], timestamp, sender, error } = msg;

    console.log("📨 Received diagnosticSources:", sources);

    if (error) {
      console.error("❌ Error in diagnosticSources:", error);
      return;
    }

    setDiagnosticSources((prev) => {
      const newSources = [...prev];

      sources.forEach((url) => {
        if (!newSources.some(src => src.url === url)) {
          const fileName = url.split("/").pop();
          newSources.push({
            title: fileName || "View Document",
            url: url,
            summary: "",
            icon: "pdf"  // or "file" if you want generic
          });
        }
      });

      return newSources;
    });


  });


    // Listen for session status updates
    s.on("sessionStatus", (status) => {
      console.log("📊 Received sessionStatus:", status);
      setSessionStatus(status);
      
      // Clear last diagnostic state if session is no longer active  
      if (!status.hasActiveSession) {
        setLastDiagnosticState(null);
      }
    });

    // Listen for document summaries
    s.on("documentSummary", (data) => {
      const { documentPath, summary, error } = data;
      console.log("📄 Received documentSummary:", { documentPath, summary: summary?.slice(0, 100) + "...", error });
      
      if (error) {
        console.error("❌ Error generating summary:", error);
        setDocumentSummaries(prev => {
          console.log("📄 Setting error summary for:", documentPath);
          return {
            ...prev,
            [documentPath]: "Unable to generate summary for this document."
          };
        });
      } else {
        setDocumentSummaries(prev => {
          console.log("📄 Setting success summary for:", documentPath);
          return {
            ...prev,
            [documentPath]: summary
          };
        });
      }
    });
    console.log("About to Disconnect SearchResponse")
    return () => s.disconnect();
  }, [username]); // Removed mode - using useRef instead to avoid socket recreation

  // Avatar event handlers
  const handleAvatarStatusChange = (status) => {
    console.log("Avatar status:", status);
    setAvatarStatus(status);
    
    // Update avatarActive based on actual session state
    if (status === "connected") {
      setAvatarActive(true);
      console.log("🎭 Avatar connected, setting avatarActive to true");
    } else if (status === "disconnected") {
      setAvatarActive(false);
      console.log("🎭 Avatar disconnected, setting avatarActive to false");
    }
  };

  // Function to send message to avatar
  const sendMessageToAvatar = (message) => {
    console.log("🎭 sendMessageToAvatar called:", { message, avatarRefExists: !!avatarRef.current, avatarActive });
    if (avatarRef.current && avatarActive) {
      console.log("✅ Calling avatar.sendMessage");
      avatarRef.current.sendMessage(message);
    } else {
      console.log("❌ Avatar ref or avatarActive not ready:", { avatarRefExists: !!avatarRef.current, avatarActive });
    }
  };

  useEffect(() => {
    // Handle auto-scroll based on message sender and mode
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      
      setTimeout(() => {
        if (lastMessage.sender === 'user' || lastMessage.sender === username) {
          // User message: scroll to bottom (all modes)
          chatBodyRef.current?.scrollTo({
            top: chatBodyRef.current.scrollHeight,
            behavior: "smooth",
          });
        } else if ((lastMessage.sender === 'bot' || lastMessage.sender === 'system' || lastMessage.sender === 'agent')) {
          // Bot, system, or live agent message: auto-scroll based on mode
          const chatBody = chatBodyRef.current;
          if (chatBody) {
            const currentScrollTop = chatBody.scrollTop;
            const scrollHeight = chatBody.scrollHeight;
            const clientHeight = chatBody.clientHeight;
            
            if (mode === "diagnostic") {
              // Diagnostic mode: show about 50% of the new message
              const targetScrollTop = Math.min(
                currentScrollTop + (clientHeight * 0.5),
                scrollHeight - clientHeight // Don't go past the bottom
              );
              
              chatBody.scrollTo({
                top: targetScrollTop,
                behavior: "smooth",
              });
            } else if (mode === "copilot") {
              // Copilot mode: always scroll to bottom for new messages
              chatBody.scrollTo({
                top: scrollHeight - clientHeight,
                behavior: "smooth",
              });
            } else {
              // Search mode: scroll to keep the last user query at the top
              const userQueries = chatBody.querySelectorAll('.user-query');
              const lastUserQuery = userQueries[userQueries.length - 1];
              if (lastUserQuery) {
                const targetScrollTop = lastUserQuery.offsetTop;
                chatBody.scrollTo({
                  top: targetScrollTop,
                  behavior: "smooth",
                });
              }
            }
          }
        }
      }, 100); // Small delay to ensure DOM is updated
    }
  }, [messages, username, mode]);



  // Video chat handlers
  const handleStartVideo = async () => {
    setVideoOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      alert("Could not access webcam: " + err.message);
      setVideoOpen(false);
    }
  };

  const handleStopVideo = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setVideoOpen(false);
  };

  // Handle image paste
  const handleImagePaste = (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          // Check file size (max 10MB)
          if (file.size > 10 * 1024 * 1024) {
            alert('Image file is too large. Please paste an image smaller than 10MB.');
            return;
          }
          
          const reader = new FileReader();
          reader.onload = (e) => {
            const base64 = e.target.result;
            setPastedImage({
              data: base64,
              name: file.name,
              type: file.type,
              size: file.size
            });
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }
  };

  // Clear pasted image
  const clearPastedImage = () => {
    setPastedImage(null);
  };

  // Drag and Drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set drag over to false if we're leaving the container entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        // Check file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          alert('Image file is too large. Please drop an image smaller than 10MB.');
          return;
        }
        
        const reader = new FileReader();
        reader.onload = (e) => {
          setPastedImage({
            data: e.target.result,
            name: file.name,
            type: file.type,
            size: file.size
          });
        };
        reader.readAsDataURL(file);
      } else {
        alert('Please drop an image file (JPEG, PNG, GIF, etc.).');
      }
    }
  };

  // File picker handler
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Image file is too large. Please select an image smaller than 10MB.');
        e.target.value = '';
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setPastedImage({
          data: e.target.result,
          name: file.name,
          type: file.type,
          size: file.size
        });
      };
      reader.readAsDataURL(file);
    } else if (file) {
      alert('Please select an image file (JPEG, PNG, GIF, etc.).');
      e.target.value = '';
    }
    // Reset the input value so the same file can be selected again
    e.target.value = '';
  };


  // Auto-resize textarea function
  const autoResizeTextarea = (textarea) => {
    if (textarea) {
      // Reset height to calculate scroll height properly
      textarea.style.height = '28px';
      
      // Calculate new height based on content
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, 28), 200); // Min 28px, max 200px
      
      // Apply the new height
      textarea.style.height = newHeight + 'px';
      
      // Keep textarea scroll position at top when it expands
      if (newHeight > 28) {
        setTimeout(() => {
          textarea.scrollTop = 0;
        }, 10);
      }
    }
  };

  // Helper function to parse timestamps specifically for Search mode
  const parseSearchTimestamp = (timestamp) => {
    if (!timestamp) return null;
    if (typeof timestamp === "number") return new Date(timestamp);
    if (typeof timestamp === "string") {
      // Handle the problematic format: "2025-07-12T20:44:28.027644"
      // This format has microseconds (6 digits) and no timezone indicator
      const microsecondsMatch = timestamp.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})\.(\d{3})\d{3}$/);
      if (microsecondsMatch) {
        // Convert microseconds to milliseconds and add UTC indicator
        return new Date(`${microsecondsMatch[1]}.${microsecondsMatch[2]}Z`);
      }
      
      // If already ISO with Z or timezone, let Date handle it
      if (timestamp.endsWith("Z") || timestamp.includes("+") || timestamp.includes("-")) {
        return new Date(timestamp);
      }
      
      // If no timezone indicator, treat as UTC
      if (timestamp.includes("T")) {
        return new Date(timestamp + "Z");
      }
      
      // Fallback
      return new Date(timestamp);
    }
    return null;
  };

  // Format timestamp function
  const formatTimestamp = (timestamp, isSearchMode = false) => {
    if (!timestamp) return '';
    
    let date;
    if (isSearchMode) {
      // Use special parsing for search mode timestamps
      date = parseSearchTimestamp(timestamp);
    } else {
      // Use standard parsing for diagnostic mode (keep existing behavior)
      date = new Date(timestamp);
    }
    
    if (!date || isNaN(date.getTime())) {
      console.warn("🕒 Invalid timestamp:", timestamp);
      return '';
    }
    
    const formatted = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    return formatted;
  };

  // Request AI summary for a document
  const requestDocumentSummary = (documentPath, queryId = null) => {
    console.log("📄 requestDocumentSummary called with:", documentPath, "queryId:", queryId);
    console.log("📄 Current documentSummaries:", documentSummaries);
    console.log("📄 Generated overviews:", generatedOverviews);
    console.log("📄 Socket exists:", !!socket);

    // Use a composite key for per-query independence
    const overviewKey = queryId ? `${documentPath}__${queryId}` : documentPath;

    if (!generatedOverviews.has(overviewKey) && socket) {
      console.log("📄 Emitting generateDocumentSummary for:", documentPath, "queryId:", queryId);
      socket.emit("generateDocumentSummary", { documentPath, queryId });
      setGeneratedOverviews(prev => new Set([...prev, overviewKey]));
    } else {
      console.log("📄 Skipping request - already generated overview or no socket");
    }
  };

  // Expose expandImage function globally for backend-generated HTML
  useEffect(() => {
    window.expandImage = (url) => {
      setZoomImageUrl(url);
    };
    return () => {
      delete window.expandImage;
    };
  }, []);

  // Format bot messages for better readability
  const formatBotMessage = (text, isSearchResponse = false, currentMode = null) => {
    if (!text || typeof text !== 'string') return text;
    let formatted = text;

    // 1. Format markdown tables to HTML tables
    formatted = formatted.replace(/Markdown Summary Table/gi, 'Summary Table');
    formatted = formatted.replace(/\(with Line Breaks\)/gi, '');

    // Detect markdown table blocks
    formatted = formatted.replace(/```[\s\S]*?\n([\s\S]*?\|[\s\S]*?\|[\s\S]*?)```/g, (match, tableContent) => {
      // Split lines and filter out empty lines
      const lines = tableContent.trim().split('\n').filter(line => line.trim());
      if (lines.length < 2) return match; // Not a valid table
      // Find header and separator
      const header = lines[0].split('|').map(cell => cell.trim()).filter(Boolean);
      const separator = lines[1];
      const rows = lines.slice(2).map(line => line.split('|').map(cell => cell.trim()).filter(Boolean));
      // Build HTML table
      let html = '<table class="border border-gray-300 rounded-lg overflow-hidden my-2 text-sm" style="table-layout:auto;width:auto;max-width:100%;"><thead><tr>';
      header.forEach(cell => {
        html += `<th class='bg-gray-100 border-b border-gray-300 px-3 py-2 font-semibold text-left'>${cell}</th>`;
      });
      html += '</tr></thead><tbody>';
      rows.forEach(row => {
        html += '<tr>';
        row.forEach(cell => {
          // Detect markdown links [text](url) → clickable anchor
          const mdLinkMatch = cell.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
          const urlMatch = cell.match(/^https?:\/\/[\w\-._~:/?#\[\]@!$&'()*+,;=%]+$/i);
          let cellContent = cell;
          if (mdLinkMatch) {
            const [, label, url] = mdLinkMatch;
            cellContent = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">${label}</a>`;
          } else if (urlMatch) {
            cellContent = `<a href="${cell}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">Link</a>`;
          }
          html += `<td class='border-b border-gray-200 px-3 py-2' style='max-width:220px;white-space:normal;word-break:break-word;'>${cellContent}</td>`;
        });
        html += '</tr>';
      });
      html += '</tbody></table>';
      return html;
    });

    // 2. Format numbered lists - more conservative approach
    formatted = formatted.replace(/(\d+\..*?)(\n\s*\n)(\d+\.\s)/gs, '$1<br/><br/>$3');

    // 3. Bold text: **text** → <strong>text</strong>
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // 4. Color-coded severity levels with better styling
    formatted = formatted.replace(/\(Severity:\s*(Critical)\)/gi, '<span style="color: #dc2626; font-weight: 600;">(Severity: $1)</span>');
    formatted = formatted.replace(/\(Severity:\s*(High)\)/gi, '<span style="color: #ea580c; font-weight: 600;">(Severity: $1)</span>');
    formatted = formatted.replace(/\(Severity:\s*(Medium)\)/gi, '<span style="color: #d97706; font-weight: 600;">(Severity: $1)</span>');
    formatted = formatted.replace(/\(Severity:\s*(Low)\)/gi, '<span style="color: #16a34a; font-weight: 600;">(Severity: $1)</span>');

    // 5. Add line breaks before standalone questions that start with capital letters
    formatted = formatted.replace(/([.!])\s+((?:Would you like|Please select|Do you want|Shall I|Would you prefer)[^?]*\?)/gi, '$1<br/><br/>$2');

    // 6. Remove markdown link format [text](url) - keep only the link part
    formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$2');

    // 7. URL detection and beautification for ALL modes (not just copilot)
    // Match only actual URLs that are NOT already wrapped in HTML tags
    const urlRegex = /(?<!<[^>]*)(https?:\/\/[^\s<>()]+)(?![^<]*>)/gi;
    formatted = formatted.replace(urlRegex, (match) => {
      // Ensure URL has protocol
      let url = match;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      
      // Keep the full URL as link text (don't clip it)
      let linkText = match;
      
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline decoration-blue-300 hover:decoration-blue-500 transition-colors duration-200">${linkText}</a>`;
    });

    // 7. Preserve existing line breaks and convert them to HTML
    formatted = formatted.replace(/\n/g, '<br/>');

    // 8. Clean up multiple consecutive br tags
    formatted = formatted.replace(/(<br\/>){3,}/g, '<br/><br/>');

    // Structure 'Analysis:' blocks: bold header, line break, then analysis text, and highlight code/values, but do NOT wrap in a card or add icon
    formatted = formatted.replace(/(^|<br\/>)(Analysis:)([\s\S]*?)(?=(<br\/>|$))/gi, (match, br, header, body) => {
      let highlightedBody = body.trim();
      // Only highlight in the analysis body (not in HTML tags)
      // Backticks: `code`
      highlightedBody = highlightedBody.replace(/`([^`]+)`/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
      // Single quotes: 'value' (not part of a word like they're)
      highlightedBody = highlightedBody.replace(/(?<=\s|^)'([^'\s][^']*?)'(?!\w)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
      // Double quotes: "value" (not part of a word)
      highlightedBody = highlightedBody.replace(/(?<=\s|^)"([^"\s][^"]*?)"(?!\w)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
      // Parentheses: (value) (not part of a word)
      highlightedBody = highlightedBody.replace(/(?<=\s|^)\(([^\s][^\)]*?)\)(?=\s|[.,;:]|$)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">($1)</span>');
      // Pipes: |value| (not part of a word)
      highlightedBody = highlightedBody.replace(/(?<=\s|^)\|([^\|\s][^\|]*?)\|(?=\s|[.,;:]|$)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
      // Now convert line breaks to <br/>
      highlightedBody = highlightedBody.replace(/\n/g, '<br/>');
      return `${br}<strong>Analysis:</strong><br/>${highlightedBody}`;
    });

    // Format command blocks: ```command``` or "Please run this command:" followed by code
    formatted = formatted.replace(/(Please run this command:)([\s\S]*?)(Then share the output with me\.)/gi, (match, prefix, command, suffix) => {
      const cleanCommand = command.trim().replace(/```/g, '').replace(/`/g, '');
      return `${prefix}<br/><div style="background: #f8f9fa; border: 1px solid #e9ecef; padding: 8px; border-radius: 4px; font-family: 'Courier New', monospace; font-size: 13px; margin: 4px 0; overflow-x: auto;">${cleanCommand}</div><br/>${suffix}`;
    });

    // Format multiple commands mixed with text - NEW ENHANCEMENT
    // Handle cases where there are multiple "Please run this command:" sections in one response
    // This handles the general case of multiple commands with text in between
    
    // First, let's handle the specific case of exactly 2 commands
    formatted = formatted.replace(/(Please run this command:)([\s\S]*?)(Then share the output with me\.)([\s\S]*?)(Please run this command:)([\s\S]*?)(Then share the output with me\.)/gi, (match, prefix1, command1, suffix1, middleText, prefix2, command2, suffix2) => {
      const cleanCommand1 = command1.trim().replace(/```/g, '').replace(/`/g, '');
      const cleanCommand2 = command2.trim().replace(/```/g, '').replace(/`/g, '');
      const formattedMiddleText = middleText.trim().replace(/\n/g, '<br/>');
      
      return `${prefix1}<br/><div style="background: #f8f9fa; border: 1px solid #e9ecef; padding: 8px; border-radius: 4px; font-family: 'Courier New', monospace; font-size: 13px; margin: 4px 0; overflow-x: auto;">${cleanCommand1}</div><br/>${suffix1}<br/><br/>${formattedMiddleText}<br/><br/>${prefix2}<br/><div style="background: #f8f9fa; border: 1px solid #e9ecef; padding: 8px; border-radius: 4px; font-family: 'Courier New', monospace; font-size: 13px; margin: 4px 0; overflow-x: auto;">${cleanCommand2}</div><br/>${suffix2}`;
    });
    
    // Now handle the general case of any number of commands mixed with text
    // Split the text into sections and process each command block
    const commandSections = formatted.split(/(Please run this command:)/gi);
    if (commandSections.length > 1) {
      let processedText = '';
      for (let i = 0; i < commandSections.length; i++) {
        if (i === 0) {
          // First section is just text before any command
          processedText += commandSections[i];
        } else if (i % 2 === 1) {
          // This is a "Please run this command:" section
          const commandText = commandSections[i + 1] || '';
          const commandMatch = commandText.match(/([\s\S]*?)(Then share the output with me\.)/);
          
          if (commandMatch) {
            const command = commandMatch[1].trim().replace(/```/g, '').replace(/`/g, '');
            const suffix = commandMatch[2];
            const remainingText = commandText.substring(commandMatch[0].length);
            
            processedText += `Please run this command:<br/><div style="background: #f8f9fa; border: 1px solid #e9ecef; padding: 8px; border-radius: 4px; font-family: 'Courier New', monospace; font-size: 13px; margin: 4px 0; overflow-x: auto;">${command}</div><br/>${suffix}${remainingText}`;
          } else {
            // Fallback if the pattern doesn't match exactly
            processedText += `Please run this command:${commandText}`;
          }
        }
      }
      formatted = processedText;
    }

    // Format code blocks with backticks
    formatted = formatted.replace(/```([\s\S]*?)```/g, (match, code) => {
      return `<div style="background: #f8f9fa; border: 1px solid #e9ecef; padding: 8px; border-radius: 4px; font-family: 'Courier New', monospace; font-size: 13px; margin: 4px 0; overflow-x: auto;">${code.trim()}</div>`;
    });

    // Add search response prefix if this is a search response
    if (isSearchResponse) {
      formatted = `<div style="display: flex; align-items: center; margin-bottom: 8px; color: #2563eb; font-weight: 600; font-size: 12px;">
        <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="margin-right: 6px;">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
        SEARCH RESULT
      </div>${formatted}`;
    }

    return formatted;
  };

  // Global keyword detection function
  const handleGlobalKeywords = (message) => {
    const trimmedMessage = message.trim().toLowerCase();
    
    console.log("🔍 Checking global keywords for:", trimmedMessage);
    
    // Escalation keywords
    if (trimmedMessage === 'escalate' || trimmedMessage === 'live agent') {
      console.log("[ESCALATION-TRIGGER-DEBUG] ========== ESCALATION KEYWORD DETECTED ==========");
      console.log("[ESCALATION-TRIGGER-DEBUG] Message:", message);
      console.log("[ESCALATION-TRIGGER-DEBUG] Trimmed message:", trimmedMessage);
      console.log("[ESCALATION-TRIGGER-DEBUG] Socket exists:", !!socket);
      
      // Add user message to chat
      setMessages((prev) => [...prev, { text: message, sender: username, timestamp: Date.now() }]);
      
      // Add immediate confirmation message
      setTimeout(() => {
        setMessages((prev) => [...prev, { 
          text: "🔄 Escalating to live agent... Please wait while we connect you to a support representative.", 
          sender: "bot",
          timestamp: Date.now()
        }]);
      }, 100);
      
      // Trigger escalation
      if (socket) {
        console.log("[ESCALATION-TRIGGER-DEBUG] Emitting triggerEscalation event");
        socket.emit("triggerEscalation", { userMessage: message });
        console.log("[ESCALATION-TRIGGER-DEBUG] triggerEscalation event emitted");
      } else {
        console.error("[ESCALATION-TRIGGER-DEBUG] Socket not available for escalation");
      }
      
      // Clear input and reset textarea
      setMessage("");
      setTimeout(() => {
        const textareas = document.querySelectorAll('textarea');
        textareas.forEach(textarea => {
          textarea.style.height = 'auto';
          textarea.style.height = '28px';
        });
      }, 0);
      
      console.log("[ESCALATION-TRIGGER-DEBUG] ========== ESCALATION TRIGGER COMPLETED ==========");
      return true; // Keyword handled
    }
    
    // Session reset keywords
    if (trimmedMessage === 'exit' || trimmedMessage === 'quit' || trimmedMessage === 'clear') {
      // Add user message to chat briefly, then reset
      setMessages((prev) => [...prev, { text: message, sender: username, timestamp: Date.now() }]);
      
      // Add confirmation message briefly
      setTimeout(() => {
        setMessages((prev) => [...prev, { 
          text: "🔄 Resetting session...", 
          sender: "bot",
          timestamp: Date.now()
        }]);
      }, 100);
      
      // Reset all chat state after a short delay
      setTimeout(() => {
        setMessages([]);
        setIsWaitingForCommand(false);
        setIsWaitingForDecision(false);
        setInRecommendation(false);
        setRecommendationStartIdx(-1);
        setSentiment(8);
        console.log("🔄 Setting mode to diagnostic from handleGlobalKeywords");
        setMode("diagnostic");
        
        // Reset avatar panel if open
        if (avatarPanelOpen) {
          setAvatarPanelOpen(false);
          if (avatarRef.current && avatarRef.current.sessionState !== "INACTIVE") {
            avatarRef.current.stopSession();
          }
        }
        
        // Emit session reset to backend
        if (socket) {
          socket.emit("resetSession");
          
          // Re-join room with user information to ensure proper participant tracking
          setTimeout(() => {
            const sessionId = socket.id;
            console.log("calling join room from ResetSession")
            socket.emit("joinRoom", { sessionId, role: "user", name: username || "User" });
          }, 100);
        }
      }, 500);
      
      // Clear input and reset textarea immediately
      setMessage("");
      setTimeout(() => {
        const textareas = document.querySelectorAll('textarea');
        textareas.forEach(textarea => {
          textarea.style.height = 'auto';
          textarea.style.height = '28px';
        });
      }, 0);
      
      return true; // Keyword handled
    }
    
    console.log("🔍 No keyword detected, returning false");
    return false; // No keyword detected
  };

  const sendMessage = () => {
    if ((!message.trim() && !pastedImage) || !socket) return;
    
    // Check for global keywords FIRST (before adding to UI)
    if (message.trim() && handleGlobalKeywords(message)) {
      return; // Keyword was handled, don't proceed with normal flow
    }
    
    // Add message to appropriate chat history based on mode
    const newMessage = { 
      text: message, 
      sender: username, 
      timestamp: Date.now(),
      isSearchQuery: mode === "search",
      sources: [],
      image: pastedImage // Include image if present
    };

    if (mode === "diagnostic") {
      setDiagnosticMessages(prev => [...prev, newMessage]);
      // Show typing indicator immediately for diagnostic mode
      setShowTypingIndicator(true);
    } else if (mode === "search") {
      setSearchMessages(prev => [...prev, newMessage]);
      // Auto-select "Answer" tab when sending a search query
      setSearchSubMode("rag");
    } else if (mode === "copilot") {
      setCopilotMessages(prev => [...prev, newMessage]);
    }
    
    // Handle different modes
    if (mode === "search") {
      // SEARCH MODE: Use RAG system
      console.log("🔍 Sending search query:", message);
      socket.emit("searchQuery", { text: message });
    } else if (mode === "copilot") {
      // COPILOT MODE: Send to copilot system using same pattern as escalation
      console.log("🤖 Sending user message to TSE agents:", message);
      console.log("🤖 Socket exists:", !!socket);
      console.log("🤖 Emitting userMessage event...");
      socket.emit("userMessage", { text: message });
      console.log("🤖 userMessage event emitted");
    } else {
      // DIAGNOSTIC MODE: Use existing diagnostic flow
      console.log("🩺 Sending diagnostic message:", message);
      
      // Update title only on first message in diagnostic mode
      if (!isTitleSet) {
        const newTitle = getTitleFromMessage(message);
        setSupportType(newTitle);
        setIsTitleSet(true);
      }
      
      // Emit diagnostic message
      socket.emit("chatMessage", { 
        text: message, 
        image: pastedImage 
      });
    }

    // Clear message and reset UI
    setMessage("");
    clearPastedImage(); // Clear pasted image after sending
    setTimeout(() => {
      const textareas = document.querySelectorAll('textarea');
      textareas.forEach(textarea => {
        textarea.style.height = 'auto';
        textarea.style.height = '28px';
      });
    }, 0);
  };

  // Only show landing text until first message
  const chatStarted = messages.length > 0;

  // Check if agent is present in participants
  const agent = participants.find((p) => p.role === "agent");
  const isAgentPresent = !!agent;

  // Add new state for search dropdown
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const searchDropdownRef = useRef(null);

  const [previousMode, setPreviousMode] = useState("diagnostic");
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  // Search source options
  const [searchSources, setSearchSources] = useState([
    {
      id: 'CaseNotes',
      icon: (
        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10"/>
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      ),
      title: 'CaseNotes',
      description: 'search for CaseNotes',
      active: true
    },
    {
      id: 'Chat History',
      icon: (
        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
          <path d="M6 12v5c3 3 9 3 12 0v-5"/>
        </svg>
      ),
      title: 'Chat History',
      description: 'Search Chat History',
      active: true
    },
    {
      id: 'Defects',
      icon: (
        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <circle cx="18" cy="5" r="3"/>
          <circle cx="6" cy="12" r="3"/>
          <circle cx="18" cy="19" r="3"/>
          <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/>
        </svg>
      ),
      title: 'Defects',
      description: 'search for Defects',
      active: true
    },
    {
      id: 'Product Docs',
      icon: (
        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M21 5H3v14h18V5zM3 10h18"/>
          <path d="M7 15h.01M11 15h.01M15 15h.01"/>
        </svg>
      ),
      title: 'Product Docs',
      description: 'search for Product Docs',
      active: true
    }
  ]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setSearchDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle search source
  const toggleSearchSource = (sourceId) => {
    setSearchSources(sources => 
      sources.map(source => ({
        ...source,
        active: source.id === sourceId ? !source.active : source.active
      }))
    );
  };

  // Helper to capitalize names
  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Check if scroll indicator should be shown
  const checkScrollIndicator = () => {
    if (mode !== "search" || !chatBodyRef.current) return;
    
    const container = chatBodyRef.current;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const scrollTop = container.scrollTop;
    
    // Show indicator if there's more content below the visible area
    const hasMoreContent = scrollHeight > clientHeight && scrollTop < (scrollHeight - clientHeight - 50);
    setShowScrollIndicator(hasMoreContent);
  };

  // Handle scroll events
  const handleScroll = () => {
    checkScrollIndicator();
  };



  // Add new state for tab hover
  const [hoveredTab, setHoveredTab] = useState(null);



  // Utility Bar JSX for reuse
  const utilityBar = (
    <div className="mt-2 w-full max-w-3xl mx-auto flex justify-center">
      <div className="flex items-center gap-3 w-full px-2 py-1">
        {/* Mode Toggle Buttons */}
        <div className="flex items-center bg-gray-100 rounded-lg p-1">
          <button
            className={`px-3 py-1 rounded-md text-xs font-medium transition ${
              mode === "diagnostic"
                ? "bg-[#06b6d4] text-white"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => setMode("diagnostic")}
            type="button"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="inline mr-1">
              <path d="M9 12l2 2 4-4"/>
              <path d="M12 2v20M2 12h20"/>
            </svg>
            vTechSupport
          </button>
          <button
            className={`px-3 py-1 rounded-md text-xs font-medium transition ${
              mode === "copilot"
                ? "bg-[#06b6d4] text-white"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => {
              console.log("🤖 Copilot mode button clicked, current mode:", mode);
              setMode("copilot");
              console.log("🤖 Mode set to copilot");
              
              // Notify TSE agents that a user has entered copilot mode
              if (socket) {
                socket.emit("userEnteredCopilot", {
                  user_sid: socket.id,
                  username: username || "User"
                });
                console.log("🤖 Notified TSE agents about copilot session");
              }
            }}
            type="button"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="inline mr-1">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            Copilot
          </button>
          <button
            className={`px-3 py-1 rounded-md text-xs font-medium transition ${
              mode === "search"
                ? "bg-[#06b6d4] text-white"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => setMode("search")}
            type="button"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="inline mr-1">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            Search
          </button>
        </div>

        {/* Session Reset Button (only show in diagnostic mode with active session) */}
        {mode === "diagnostic" && sessionStatus.hasActiveSession && (
          <button 
            title="Reset Diagnostic Session"
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition"
            onClick={() => {
                                      // eslint-disable-next-line no-restricted-globals
              if (confirm("🔄 Reset diagnostic session? This will clear all troubleshooting progress.")) {
                socket?.emit("resetSession");
                setMessages(prev => [...prev, { 
                  text: "🔄 Diagnostic session reset. Starting fresh...", 
                  sender: "system", 
                  timestamp: Date.now() 
                }]);
                
                // Clear diagnostic state
                setLastDiagnosticState(null);
              }
            }}
            type="button"
          >
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
              <path d="M3 3v5h5"/>
            </svg>
            Reset
          </button>
        )}

        {/* Sources Button - Show when sources are available AND in diagnostic mode */}
        {diagnosticSources.length > 0 && mode === "diagnostic" && (
          <button
            title={`View Sources (${diagnosticSources.length})`}
            className={`flex items-center gap-2 px-3 py-1 rounded-lg transition ${
              sourcesPanelOpen && sidebarMode === "sources"
                ? "bg-[#06b6d4] text-white" 
                : "bg-gray-100 text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => {
              setSidebarMode("sources");
              setSourcesPanelOpen(!sourcesPanelOpen);
            }}
            type="button"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
            </svg>
            <span className="text-xs font-medium">Sources</span>
            <span className="bg-white bg-opacity-30 text-xs px-1.5 py-0.5 rounded-full min-w-[16px] text-center">
              {diagnosticSources.length}
            </span>
          </button>
        )}

        {/* Set Sources for Search Button */}
        <button
          title="Set Sources for Search"
          className={`hover:bg-[#E6F0EF] p-2 rounded-lg transition ${
            sourcesPanelOpen && sidebarMode === "search" ? "bg-[#E6F0EF] ring-2 ring-[#06b6d4]" : ""
          }`}
          onClick={() => {
            setSidebarMode("search");
            setSourcesPanelOpen(true);
          }}
          type="button"
        >
          <svg width="20" height="20" fill="none" stroke="#253439" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
        </button>

        {/* Attach Files */}
        <button 
          title="Attach Image" 
          className="hover:bg-[#E6F0EF] p-2 rounded-lg transition" 
          onClick={() => document.getElementById('imageFileInput')?.click()}
          type="button"
        >
          <svg width="20" height="20" fill="none" stroke="#253439" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M21.44 11.05a5.5 5.5 0 0 1-7.78 7.78l-8.49-8.49a4 4 0 0 1 5.66-5.66l7.78 7.78" />
          </svg>
        </button>
        {/* Hidden file input for image selection */}
        <input
          id="imageFileInput"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
        />
        {/* Dictation (Mic) */}
        <button title="Dictation" className="hover:bg-[#E6F0EF] p-2 rounded-lg transition" type="button">
          <svg width="20" height="20" fill="none" stroke="#253439" strokeWidth="2" viewBox="0 0 24 24">
            <rect x="9" y="2" width="6" height="12" rx="3" />
            <path d="M5 10v2a7 7 0 0 0 14 0v-2" />
            <path d="M12 19v3m-4 0h8" />
          </svg>
        </button>
        {/* Voice Mode */}
        <button title="Voice Mode" className="hover:bg-[#E6F0EF] p-2 rounded-lg transition" type="button">
          <svg width="20" height="20" fill="none" stroke="#253439" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M9 9v6l7-3-7-3z" />
            <circle cx="12" cy="12" r="10" />
          </svg>
        </button>
        {/* AI Agent Video Mode */}
        <button
          title="AI Agent Video"
          className={`hover:bg-[#E6F0EF] p-2 rounded-lg transition ${
            avatarPanelOpen ? 'bg-[#E6F0EF] ring-2 ring-[#06b6d4]' : ''
          }`}
          onClick={() => {
            const newPanelOpen = !avatarPanelOpen;
            setAvatarPanelOpen(newPanelOpen);
            console.log("🎭 Avatar panel toggled to:", newPanelOpen);
            
            if (newPanelOpen) {
              console.log("🎭 Opening avatar panel - auto-clicking Start Avatar button");
              setTimeout(() => {
                if (avatarRef.current && avatarRef.current.clickStartButton) {
                  avatarRef.current.clickStartButton();
                }
              }, 100);
            } else {
              console.log("🎭 Closing avatar panel - stopping session");
              if (avatarRef.current && avatarRef.current.sessionState !== "INACTIVE") {
                avatarRef.current.stopSession();
              }
            }
          }}
          type="button"
        >
          <img 
            src="/avatar_video_png.png" 
            alt="AI Agent Video" 
            width="20" 
            height="20" 
          />
        </button>
        {/* Dynamic Sentiment Speedometer */}
        <div className="flex-1"></div>
        <div className="flex items-center ml-auto">
          <Speedometer score={sentiment} size={60} />
        </div>
      </div>
    </div>
  );

  const handleSidebarClick = (path) => {
    navigate(path);
  };

  useEffect(() => {
  console.log("🔍 searchSourcesMap updated:", searchSourcesMap);
}, [searchSourcesMap]);

  // Set up scroll event listener and check scroll indicator
  useEffect(() => {
    const container = chatBodyRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [mode]);

  // Check scroll indicator when search messages change
  useEffect(() => {
    if (mode === "search") {
      // Small delay to ensure DOM is updated
      setTimeout(checkScrollIndicator, 100);
    }
  }, [searchMessages, mode]);

  return (
    <div className="flex h-full w-full transition-colors duration-300 bg-[#F3F3ED]">
      {/* Add typewriter effect CSS */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .typewriter-effect {
            overflow: hidden;
            white-space: nowrap;
            animation: typing 2s steps(40, end);
          }
          
          @keyframes typing {
            from { width: 0 }
            to { width: 100% }
          }
          

        `
      }} />
      <SideBar mode="user" onLogout={onLogout} onLinkClick={handleSidebarClick} />
      
      {/* Chat Title - Move to top left, above the white background */}
      <div className="fixed top-3 left-24 z-[100]">
        <h1 className={`text-sm font-bold text-[#253439] tracking-wide uppercase ${isTyping ? 'after:content-["|"] after:ml-1 after:animate-pulse' : ''}`}
          style={{ letterSpacing: '0.05em' }}>
          {displayTitle}
        </h1>
      </div>

      {/* Move user count to the white background with higher z-index */}
      <div className="fixed top-3 right-8 z-[150] flex items-center gap-2">
        {/* Dynamic participant display */}
        {(() => {
          if (!chatStarted) {
            // Only user on landing
            return (
              <div className="flex items-center px-3 py-1 rounded-full text-white font-medium shadow bg-[#06b6d4]" style={{ fontSize: 14 }} title={username || "User"}>
                <span className="capitalize">{username || "User"}</span>
              </div>
            );
          }
          // After chat starts, show participants based on mode
          const user = participants.find((p) => p.role === "user");
          const bot = participants.find((p) => p.role === "bot");
          const agent = participants.find((p) => p.role === "agent");
          
          // In search mode, only show user (no John) - just user button
          if (mode === "search") {
            return [
              user && (
                <div key={user.id} className="flex items-center px-3 py-1 rounded-full text-white font-medium shadow bg-[#06b6d4]" style={{ fontSize: 14 }} title={capitalize(user.name)}>
                  <span>{capitalize(user.name)}</span>
                </div>
              )
            ];
          }
          
          // In copilot mode, show user and any TSE agents - top right buttons
          if (mode === "copilot") {
            // Need to know if TSECopilot live agent has joined. Extract from localStorage
            var TseAgentname = localStorage.getItem('TseAgentName');
            var isTseAgentPresent = localStorage.getItem('IsTseAgentPresent');
            return [
              user && (
                <div key={user.id} className="flex items-center px-3 py-1 rounded-full text-white font-medium shadow bg-[#06b6d4]" style={{ fontSize: 14 }} title={capitalize(user.name)}>
                  <span>{capitalize(user.name)}</span>
                </div>
              ),
              isTseAgentPresent && (
                <div key={bot.id} className="flex items-center px-3 py-1 rounded-full text-white font-medium shadow bg-purple-600" style={{ fontSize: 14 }} title={capitalize(TseAgentname)}>
                  <span>{capitalize(TseAgentname)}</span>
                </div>
              )
            ];
          }
          
          // In diagnostic mode, show user and John - top right buttons
          return [
            user && (
              <div key={user.id} className="flex items-center px-3 py-1 rounded-full text-white font-medium shadow bg-[#06b6d4]" style={{ fontSize: 14 }} title={capitalize(user.name)}>
                <span>{capitalize(user.name)}</span>
              </div>
            ),
            bot && (
              <div key={bot.id} className="flex items-center px-3 py-1 rounded-full text-white font-medium shadow bg-[#06b6d4]" style={{ fontSize: 14 }} title={capitalize(bot.name)}>
                <span>{capitalize(bot.name)}</span>
              </div>
            ),
            agent && (
              <div key={agent.id} className="flex items-center px-3 py-1 rounded-full text-white font-medium shadow bg-purple-600" style={{ fontSize: 14 }} title={capitalize(agent.name)}>
                <span>{capitalize(agent.name)}</span>
              </div>
            )
          ];
        })()}
      </div>

      {/* Main content area with Perplexity-like rounded border */}
      <div className="flex-1 flex flex-col relative transition-all duration-300 ease-in-out p-1">
        {/* Rounded container with border - made much larger */}
        <div className="flex-1 bg-white rounded-2xl border-2 border-[#F3F3ED] shadow-sm overflow-hidden relative">
          {/* === CHAT SCROLL AREA === */}
          <div className="flex flex-col flex-1 items-center w-full h-full">
            {/* Fade line at top for messages */}
            <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-white to-transparent z-20 rounded-t-2xl"></div>

            {/* Scroll Indicator - Only show in search mode when there's more content */}
            {showScrollIndicator && mode === "search" && (
              <div className="absolute bottom-24 right-40 z-50">
                <div className="bg-[#06b6d4] rounded-full shadow-lg border border-[#06b6d4] p-2 animate-bounce">
                  <svg width="16" height="16" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M7 13l5 5 5-5"/>
                    <path d="M7 6l5 5 5-5"/>
                  </svg>
                </div>
              </div>
            )}



            {/* Main content area */}
            <main
              ref={chatBodyRef}
              className="w-full max-w-4xl flex-1 overflow-y-auto flex flex-col px-6 py-6 hide-scrollbar"
              style={{
                minHeight: "400px",
                maxHeight: "calc(100vh - 140px)", // Even more available space
                background: "#FFFFFF",
                marginTop: "16px", // Consistent top margin for both modes
                marginBottom: "10px", // Much smaller bottom margin for more space
                position: "relative",
                zIndex: 10
              }}
            >
              <div className="w-full">
                                {mode === "search" ? (
                  /* Search Mode - Perplexity-like UI with Multiple Queries */
                  <div className="w-full space-y-8">
                    {/* Empty State for Search */}
                    {searchMessages.length === 0 && (
                      <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                        <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24" className="mb-4">
                          <circle cx="11" cy="11" r="8"/>
                          <path d="m21 21-4.35-4.35"/>
                        </svg>
                        <p className="text-lg font-medium mb-2">Search the knowledge base</p>
                        <p className="text-sm text-center max-w-md">
                          Ask any question and get answers from our comprehensive knowledge base with cited sources.
                        </p>
                      </div>
                    )}

                    {/* Process search messages to group queries with their responses */}
                    {(() => {
                      const searchGroups = [];
                      let currentGroup = null;
                      
                      searchMessages.forEach((msg, idx) => {
                        if (msg.sender === username) {
                          // Start new group with user query
                          if (currentGroup) {
                            searchGroups.push(currentGroup);
                          }
                          currentGroup = {
                            query: msg,
                            responses: [],
                            sources: []
                          };
                        } else if (currentGroup) {
                          // Add response to current group
                          currentGroup.responses.push(msg);
                              if (msg.sources?.length > 0) {
                                currentGroup.sources = msg.sources;
                              }
                        }
                      });
                      
                      // Add the last group if it exists
                      if (currentGroup) {
                        searchGroups.push(currentGroup);
                      }
                      
                      return searchGroups.map((group, groupIdx) => (
                        <div key={groupIdx} className="w-full border-b border-gray-100 pb-8 last:border-b-0">
                          {/* Search Query Display with Typewriter Effect */}
                          <div className="mb-6">
                            <h2 className={`text-2xl font-semibold text-[#253439] mb-2 user-query ${groupIdx === searchGroups.length - 1 ? 'typewriter-effect' : ''}`}>
                              {group.query.text}
                            </h2>
                            
                            {/* Dynamic Search Subtabs */}
                            <div className="flex items-center mb-4 border-b border-gray-200">
                              {/* Answer Tab */}
                              <button
                                onClick={() => setSearchSubMode("rag")}
                                className={`px-4 py-2 text-sm font-medium transition ${
                                  searchSubMode === "rag"
                                    ? "border-b-2 border-[#06b6d4] text-[#06b6d4]"
                                    : "text-gray-500 hover:text-[#253439]"
                                }`}
                              >
                                Summary
                              </button>
                              
                              {/* Sources Tab (All Sources) */}
                              <button
                                onClick={() => setSearchSubMode("sources")}
                                className={`px-4 py-2 text-sm font-medium transition ${
                                  searchSubMode === "sources"
                                    ? "border-b-2 border-[#06b6d4] text-[#06b6d4]"
                                    : "text-gray-500 hover:text-[#253439]"
                                }`}
                              >
                                Sources ({group.sources?.length || 0})
                              </button>

                              {/* Dynamic Category Tabs */}
                              {(() => {
                                if (!group.sources?.length) return null;
                                
                                // Group sources by category
                                const sourcesByCategory = group.sources.reduce((acc, source) => {
                                  const category = source.category || "Product Documents";
                                  if (!acc[category]) acc[category] = [];
                                  acc[category].push(source);
                                  return acc;
                                }, {});

                                const categories = Object.keys(sourcesByCategory);
                                
                                return categories.map((category) => {
                                  const colors = getCategoryColor(category);
                                  const isActive = searchSubMode === category;
                                  return (
                                    <button
                                      key={category}
                                      onClick={() => setSearchSubMode(category)}
                                      className={`px-4 py-2 text-sm font-medium transition rounded-lg mx-1 ${colors.bg} ${colors.text} ${colors.border} ${colors.hover} ${isActive ? 'ring-2 ring-[#06b6d4]' : ''}`}
                                      style={{ minWidth: 110 }}
                                    >
                                      {categoryDisplayNames[category] || category} ({sourcesByCategory[category].length})
                                    </button>
                                  );
                                });
                              })()}
                            </div>

                            {/* Sources Placeholder Blocks - Only show for Answer tab */}
                          {searchSubMode === "rag" && (
                          <div className="mb-6">
                            <div className="flex flex-wrap gap-3 mb-4">
                              {group.sources?.length > 0 &&
                                group.sources.slice(0, 4)
                                  .map((source, i) => (
                                    <a
                                      key={i}
                                      href={source.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-600 border hover:bg-gray-100 transition max-w-[160px]"
                                    >
                                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center mr-2 flex-shrink-0 ${getCategoryColor(getCategoryFromDocumentName(source.name)).bg}`}>
                                        {(() => {
                                          const categoryColors = getCategoryColor(getCategoryFromDocumentName(source.name));
                                          return source.icon === "pdf" ? (
                                            <svg
                                              width="12"
                                              height="12"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                              viewBox="0 0 24 24"
                                              className={categoryColors.iconColor}
                                            >
                                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                              <polyline points="14,2 14,8 20,8" />
                                              <line x1="16" y1="13" x2="8" y2="13" />
                                              <line x1="16" y1="17" x2="8" y2="17" />
                                              <polyline points="10,9 9,9 8,9" />
                                            </svg>
                                          ) : (
                                            <div className={`w-3 h-3 ${categoryColors.bg.replace('bg-', 'bg-').replace('-100', '-300')} rounded`}></div>
                                          );
                                        })()}
                                      </div>
                                      <span className="truncate">{source.name || "PDF"}</span>
                                    </a>
                                  ))}
                            </div>
                          </div>
                        )}

                          {/* Sources Content Display */}
                          <div className="max-w-full overflow-hidden">
                          {(() => {
                            // Helper function to render sources
                            const renderSources = (sourcesToRender, showCategoryBadge = true, showTopSourceSummary = false) => (
                              <div className="space-y-3 w-full max-w-full">
                                {sourcesToRender.map((source, i) => (
                                  <div key={i} className="w-full max-w-full">
                                    <a
                                      href={source.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-start p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition w-full max-w-full"
                                    >
                                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 ${getCategoryColor(getCategoryFromDocumentName(source.name)).bg}`}>
                                        {(() => {
                                          const categoryColors = getCategoryColor(getCategoryFromDocumentName(source.name));
                                          return source.icon === "pdf" ? (
                                            <svg
                                              width="14"
                                              height="14"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                              viewBox="0 0 24 24"
                                              className={categoryColors.iconColor}
                                            >
                                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                              <polyline points="14,2 14,8 20,8" />
                                              <line x1="16" y1="13" x2="8" y2="13" />
                                              <line x1="16" y1="17" x2="8" y2="17" />
                                              <polyline points="10,9 9,9 8,9" />
                                            </svg>
                                          ) : (
                                            <div className={`w-4 h-4 ${categoryColors.bg.replace('bg-', 'bg-').replace('-100', '-300')} rounded`}></div>
                                          );
                                        })()}
                                      </div>
                                      <div className="flex-1 min-w-0 overflow-hidden">
                                        <h3 className="text-sm font-medium text-gray-900 truncate">
                                          {source.name || "PDF"}
                                        </h3>
                                        <p className="text-xs text-gray-500 mt-1 line-clamp-2 break-words">
                                          {source.summary || "No summary available"}
                                        </p>
                                        {showCategoryBadge && (
                                          <div className="mt-2">
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                              {source.category || "Product Documents"}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </a>
                                    
                                    {/* AI-Generated Summary for Top Source in Category Tabs */}
                                    {showTopSourceSummary && i === 0 && (() => {
                                      // Extract document filename from link
                                      const documentPath = source.link?.split('/').pop();
                                      const aiSummary = documentSummaries[documentPath];
                                      
                                      // Request summary if not already available
                                      if (!aiSummary && documentPath) {
                                        requestDocumentSummary(documentPath, group.query.text);
                                      }
                                      
                                      return (
                                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                                          <div className="flex items-start">
                                            <div className="flex-shrink-0">
                                              <svg className="h-4 w-4 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                              </svg>
                                            </div>
                                            <div className="ml-2 flex-1">
                                              <h4 className="text-sm font-medium text-blue-900">AI Summary</h4>
                                              <div className="mt-1 text-sm text-blue-800">
                                                {aiSummary ? (
                                                  <p className="leading-relaxed">{aiSummary}</p>
                                                ) : (
                                                  <div className="flex items-center space-x-2">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                    <span className="text-xs text-blue-600">Generating summary...</span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ))}
                              </div>
                            );

                            // Handle different tab modes
                            if (searchSubMode === "sources") {
                              // Show all sources
                              return (
                                <div className="mb-6">
                                  {group.sources?.length > 0 ? (
                                    renderSources(group.sources, true)
                                  ) : (
                                    <div className="text-center py-8 text-gray-500">
                                      <svg
                                        width="48"
                                        height="48"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1"
                                        viewBox="0 0 24 24"
                                        className="mx-auto mb-3"
                                      >
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14,2 14,8 20,8" />
                                      </svg>
                                      <p>No sources available yet</p>
                                      <p className="text-xs mt-1">Sources will appear after you search</p>
                                    </div>
                                  )}
                                </div>
                              );
                            } else if (searchSubMode !== "rag" && group.sources?.length > 0) {
                              // Show category-specific sources
                              const categoryName = searchSubMode;
                              const categorySources = group.sources.filter(
                                source => (source.category || "Product Documents") === categoryName
                              );

                              return (
                                <div className="mb-6">
                                  {categorySources.length > 0 && (
                                    <div>
                                      {/* Top Source with AI Summary (unchanged) */}
                                      <div className="mb-6 p-5 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-100 shadow-sm max-w-[85%] self-start overflow-hidden">
                                        <div className="flex items-center mb-3">
                                          <h4 className="text-lg font-semibold text-gray-900 mr-2">Overview</h4>
                                          <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">Generated</div>
                                        </div>
                                        <div className="text-gray-700 leading-relaxed">
                                          {(() => {
                                            const firstSource = categorySources[0];
                                            const documentPath = firstSource.link?.split('/').pop();
                                            console.log("🔍 Document path extraction:", { 
                                              originalLink: firstSource.link, 
                                              extractedPath: documentPath,
                                              hasSummary: !!documentSummaries[documentPath]
                                            });
                                            const aiSummary = documentSummaries[documentPath];
                                            if (!aiSummary && documentPath) {
                                              console.log("📄 Requesting summary for document path:", documentPath);
                                              requestDocumentSummary(documentPath, group.query.text);
                                            }
                                            return aiSummary ? <div className="prose prose-sm max-w-none"><p>{aiSummary}</p></div> : (
                                              <div className="flex items-center space-x-3">
                                                <div className="flex space-x-1">
                                                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                                                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                                                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                                                </div>
                                                <span className="text-sm text-gray-600">Generating overview...</span>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                      {/* Top Source Card */}
                                      <div className="w-full">
                                        <a
                                          href={categorySources[0].link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-start p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition w-full max-w-[85%] overflow-hidden mb-2"
                                        >
                                          <div className="text-blue-600 mr-3 mt-1 flex-shrink-0">
                                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                              <polyline points="14,2 14,8 20,8" />
                                            </svg>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <h3 className="text-sm font-medium text-gray-900 mb-1 truncate">{categorySources[0].name || "PDF"}</h3>
                                            <p className="text-xs text-gray-500 leading-relaxed" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>{categorySources[0].summary || "No summary available"}</p>
                                          </div>
                                        </a>
                                      </div>
                                      {/* Other Related Source Section */}
                                      {categorySources.length > 1 && (
                                        <div className="mt-4">
                                          <div className="text-xs font-semibold text-gray-600 mb-2">Other Related Source</div>
                                          <div className="space-y-2">
                                            {categorySources.slice(1).map((source, i) => (
                                              <div key={i} className="w-full">
                                                <a
                                                  href={source.link}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="flex items-start p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition w-full max-w-[85%] overflow-hidden"
                                                >
                                                  <div className="text-blue-600 mr-3 mt-1 flex-shrink-0">
                                                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                      <polyline points="14,2 14,8 20,8" />
                                                    </svg>
                                                  </div>
                                                  <div className="flex-1 min-w-0">
                                                    <h3 className="text-sm font-medium text-gray-900 mb-1 truncate">{source.name || "PDF"}</h3>
                                                    <p className="text-xs text-gray-500 leading-relaxed" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>{source.summary || "No summary available"}</p>
                                                  </div>
                                                </a>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            return null;
                          })()}
                          </div>


                          </div>

                          {/* Search Results Area - Only show in Answer tab */}
                          {searchSubMode === "rag" && group.responses.length > 0 && (
                            <div className="space-y-4">
                              {group.responses.map((msg, idx) => (
                                <div key={idx} className="w-full flex flex-col mb-4 items-start">
                                  <div className="px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm bg-white text-cyan-900 border-cyan-300">
                                    <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text, msg.isSearchResponse, "search") }} />
                                  </div>
                                  <div className="mt-1 text-xs text-gray-400 font-medium">
                                    {msg.sender === "bot" ? `${formatTimestamp(msg.timestamp || Date.now(), true)}` : `${capitalize(msg.sender || "")} ${formatTimestamp(msg.timestamp || Date.now(), true)}`}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ));
                    })()}
                  </div>
                ) : mode === "copilot" ? (
                  /* Copilot Mode - Direct TSE Agent Communication - Chat message for First page of Copilot button hit*/
                  <div className="w-full">
                    {/* Empty State for Copilot */}
                    {copilotMessages.length === 0 && (
                      <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                        <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24" className="mb-4">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                        <p className="text-lg font-medium mb-2">TSE Copilot Mode</p>
                        <p className="text-sm text-center max-w-md">
                          Connect directly with TSE agents for advanced technical support and collaboration.
                        </p>
                      </div>
                    )}

                    {/* Copilot Messages for Chat Sender */}
                    {copilotMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        className="w-full flex flex-col mb-4 items-start"
                      >
                        <div
                          className={`px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm ${
                            msg.sender === username
                              ? "bg-[#06b6d4] text-white border-[#06b6d4]"
                              : msg.sender === "agent"
                                ? "bg-purple-50 text-purple-900 border-purple-300"
                                : msg.sender === "system"
                                  ? "bg-green-50 text-green-900 border-green-300"
                                  : "bg-white text-cyan-900 border-cyan-300"
                          }`}
                          style={{ 
                            whiteSpace: "pre-line",
                            fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                            fontSize: "14px"
                          }}
                        >
                          {msg.sender === username ? (
                            <span className="whitespace-pre-wrap">{msg.text}</span>
                          ) : (
                            <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text, false, "copilot") }} />
                          )}
                        </div>
                        <div className="mt-1 text-xs text-gray-400 font-medium" style={{maxWidth: '85%'}}>
                          {msg.sender === username
                            ? `You ${formatTimestamp(msg.timestamp || Date.now())}`
                            : msg.sender === "agent"
                              // ? `${capitalize("Chris")} ${formatTimestamp(msg.timestamp || Date.now())}`
                              ? `${capitalize(localStorage.getItem('TseAgentName'))} ${formatTimestamp(msg.timestamp || Date.now())}`
                              : `${capitalize(msg.sender || "")} ${formatTimestamp(msg.timestamp || Date.now())}`}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Diagnostic Mode - Original Layout */
                  <>
                    {messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className="w-full flex flex-col mb-4 items-start"
                      >
                        <div
                          data-sender={msg.sender}
                          className={`px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm ${
                            msg.sender === username
                              ? "bg-[#06b6d4] text-white border-[#06b6d4]"
                              : msg.sender === "agent"
                                // ? "bg-white text-gray-900 border-[#00008b]"
                                ? "bg-purple-50 text-purple-900 border-purple-300"
                                : (
                                    inRecommendation && msg.sender === "bot" && recommendationStartIdx !== null && idx >= recommendationStartIdx
                                      ? "bg-green-100 text-green-900 border-green-300"
                                      : "bg-white text-cyan-900 border-cyan-300"
                                  )
                          }`}
                          style={{ 
                            whiteSpace: "pre-line",
                            fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                            fontSize: "14px"
                          }}
                        >
                          {msg.sender === username ? (
                            <>
                              {/* Display image if present */}
                              {msg.image && (
                                <div className="mb-3">
                                  <img 
                                    src={msg.image.data} 
                                    alt="User uploaded image" 
                                    className="max-w-full h-auto rounded-lg border max-h-64 object-contain"
                                  />
                                </div>
                              )}
                              {/* Display text */}
                              {(/Router#|\n.*\n|\t|\s{2,}/.test(msg.text) ? (
                                <pre style={{ fontFamily: 'IBM Plex Mono, Menlo, monospace', fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: 'none', border: 'none', padding: 0 }}>{msg.text}</pre>
                              ) : (
                                <span className="whitespace-pre-wrap">{msg.text}</span>
                              ))}
                              </>
                          ) : (
                            // Bot message without images - apply formatting
                            <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text, msg.isSearchResponse, "diagnostic") }} />
                          )}
                        </div>
                        <div className="mt-1 text-xs text-gray-400 font-medium" style={{maxWidth: '85%'}}>
                          {msg.sender === username
                            ? `You ${formatTimestamp(msg.timestamp || Date.now())}`
                            : msg.sender === "bot"
                              ? `${msg.sender_name || (participants.find(p => p.role === "bot")?.name) || "AI Agent"} ${formatTimestamp(msg.timestamp || Date.now())}`
                              : msg.sender === "agent"
                                // ? `${capitalize(msg.agentName || "Live Agent")} ${formatTimestamp(msg.timestamp || Date.now())}`
                                ? `${capitalize((participants.find(p => p.role === "agent")?.name)|| "Live Agent")} ${formatTimestamp(msg.timestamp || Date.now())}`
                                : `${capitalize(msg.sender || "")} ${formatTimestamp(msg.timestamp || Date.now())}`}
                        </div>
                      </div>
                    ))}
                    
                    {/* Typing indicator for diagnostic mode */}
                    {showTypingIndicator && !escalationActive && mode === "diagnostic" && (
                      <div className="w-full flex flex-col mb-4 items-start">
                        <div className="px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm bg-white text-cyan-900 border-cyan-300">
                          <div className="flex items-center space-x-2">
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                            </div>
                            <span className="text-sm text-gray-500">{getCurrentAgentName()} is typing...</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </main>
          </div>
        </div>

        {/* Fade gradient above chat input */}
        <div
          className="fixed pointer-events-none"
          style={{
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: '64rem', // Match max-w-4xl
            height: '40px', // Reduced height
            bottom: '120px', // Adjusted position
            background: 'linear-gradient(to top, white 0%, white 40%, transparent 100%)',
            zIndex: 40
          }}
        />

        {/* Fixed Chat Input and Utility Bar */}
        <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 bg-white z-50 w-full max-w-4xl">
          <div className="w-full mx-auto px-6 pb-4">
            {/* Chat Input */}
            <div 
              className={`bg-white rounded-xl border shadow-md flex items-start px-6 py-4 transition-all duration-200 relative ${
                isDragOver 
                  ? 'border-blue-500 bg-blue-50 shadow-lg scale-[1.02]' 
                  : 'border-[#06b6d4]'
              }`}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
            >
              {/* Drag and Drop Overlay */}
              {isDragOver && (
                <div className="absolute inset-0 bg-blue-500 bg-opacity-10 rounded-xl flex items-center justify-center pointer-events-none border-2 border-dashed border-blue-400">
                  <div className="text-blue-600 font-medium text-lg flex items-center gap-2 bg-white bg-opacity-90 px-4 py-2 rounded-lg">
                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                    Drop image here
                  </div>
                </div>
              )}

              {/* Pasted Image Preview */}
              {pastedImage && (
                <div className="mr-3 relative">
                  <img 
                    src={pastedImage.data} 
                    alt="Pasted image" 
                    className="w-16 h-16 object-cover rounded-lg border"
                  />
                  <button
                    onClick={clearPastedImage}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition"
                    type="button"
                  >
                    ×
                  </button>
                </div>
              )}
              <textarea
                className="flex-1 bg-transparent border-none outline-none text-[#253439] placeholder-[#A2A9AF] focus:ring-0 resize-none overflow-y-auto"
                style={{ 
                  minHeight: '28px', 
                  maxHeight: '200px', 
                  height: '28px',
                  fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                  fontSize: "14px"
                }}
                placeholder={
                  pastedImage
                    ? "📷 Image ready! Type a message or press Enter to send..."
                    : mode === "search"
                    ? "🔍 Search knowledge base..."
                    : isWaitingForCommand
                    ? "Enter command output…"
                    : isWaitingForDecision
                    ? "Type yes or no…"
                    : "Ask follow-up, or type a new question…"
                }
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  autoResizeTextarea(e.target);
                }}
                onPaste={handleImagePaste}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />
              {/* Video Button - Only show when agent is present */}
              {isAgentPresent && (
                <button
                  className="ml-2 p-2 rounded-full text-[#06b6d4] hover:bg-[#E6F0EF] transition"
                  aria-label="Video Chat"
                  onClick={videoOpen ? handleStopVideo : handleStartVideo}
                  type="button"
                >
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <rect x="2" y="6" width="14" height="12" rx="2" />
                    <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"/>
                  </svg>
                </button>
              )}
              {/* Send Button */}
              <button
                className={`ml-2 rounded-lg p-2 transition ${
                  pastedImage || message.trim()
                    ? "bg-[#06b6d4] hover:bg-cyan-700"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
                aria-label="Send"
                onClick={sendMessage}
                disabled={!pastedImage && !message.trim()}
                type="button"
              >
                <svg width="20" height="20" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M22 2 11 13" />
                  <path d="M22 2l-7 20-4-9-9-4z" />
                </svg>
              </button>
            </div>

            {/* Utility Bar with Mode Toggle */}
            {utilityBar}
          </div>
        </div>

        {/* Sources Panel - Bottom-right popup */}
        <div 
          className="fixed right-4 bg-[#FAFAF6] border border-[#EBECE6] rounded-lg shadow-lg transition-all duration-300 ease-in-out flex flex-col z-40"
          style={{ 
            width: '300px',
            maxHeight: '400px',
            bottom: '35%', // Position it 35% from the bottom
            transform: sourcesPanelOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
            opacity: sourcesPanelOpen ? 1 : 0,
            visibility: sourcesPanelOpen ? 'visible' : 'hidden'
          }}
        >
          <div className="flex items-center justify-between p-3 border-b border-[#EBECE6]">
            <h2 className="text-base font-semibold text-[#253439]">
              {sidebarMode === "sources" ? "Sources" : "Search Sources"}
            </h2>
            <button
              onClick={() => setSourcesPanelOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#E6F0EF] transition"
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[#253439]"
              >
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Fix: wrap sidebarMode conditional in curly braces for valid JSX */}
          {sidebarMode === "search" ? (
            // Search Sources Content
            <div className="flex-1 overflow-y-auto">
              {searchSources.map((source) => (
                <div
                  key={source.id}
                  className="flex items-center justify-between px-4 py-3 hover:bg-[#E6F0EF] cursor-pointer border-b border-[#EBECE6]"
                  onClick={() => toggleSearchSource(source.id)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-[#06b6d4]">{source.icon}</span>
                    <div>
                      <div className="text-[#253439] text-sm font-medium">{source.title}</div>
                      <div className="text-[#6B7280] text-xs">{source.description}</div>
                    </div>
                  </div>
                  <div className={`w-10 h-6 rounded-full p-1 transition-colors duration-200 ease-in-out ${source.active ? 'bg-[#06b6d4]' : 'bg-gray-300'}`}>
                    <div className={`w-4 h-4 rounded-full transition-transform duration-200 ease-in-out transform ${source.active ? 'translate-x-4 bg-white' : 'translate-x-0 bg-white'}`}></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Diagnostic Sources Content
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex flex-col space-y-3">
                {diagnosticSources.map((src, idx) => (
                  <a
                    key={idx}
                    href={src.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center bg-white hover:bg-[#E6F0EF] transition rounded-lg p-3 shadow-sm border border-[#EBECE6]"
                    style={{ textDecoration: "none" }}
                  >
                    {/* Icon logic */}
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center mr-2 flex-shrink-0 ${getCategoryColor(getCategoryFromDocumentName(src.title)).bg}`}>
                      {(() => {
                        const categoryColors = getCategoryColor(getCategoryFromDocumentName(src.title));
                        return src.icon === "pdf" ? (
                          <svg
                            width="14"
                            height="14"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            className={categoryColors.iconColor}
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14,2 14,8 20,8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10,9 9,9 8,9" />
                          </svg>
                        ) : (
                          <div className={`w-3 h-3 ${categoryColors.bg.replace('bg-', 'bg-').replace('-100', '-300')} rounded`}></div>
                        );
                      })()}
                    </div>

                      {/* Title */}
                      <span className="text-sm text-[#253439] line-clamp-2">
                        {src.title}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* === AVATAR COMPONENT === */}
          {avatarPanelOpen && (
            <div 
              className="fixed flex flex-col p-4 pt-12 transition-all duration-300 ease-in-out z-[140]"
              style={{ top: '8px', right: '8px', width: '320px' }}
            >
              {/* Avatar Container */}
              <div className="relative w-full rounded-lg overflow-hidden border border-[#EBECE6]">
                <InteractiveAvatar
                  ref={avatarRef}
                  onStatusChange={handleAvatarStatusChange}
                  avatarId={botAvatarId}
                />
                {/* Close button overlay */}
                <button
                  onClick={() => {
                    setAvatarPanelOpen(false);
                    console.log("🎭 Closed avatar panel");
                  }}
                  className="absolute top-2 right-2 w-6 h-6 bg-black bg-opacity-50 rounded-full flex items-center justify-center text-white hover:bg-opacity-70 transition z-10"
                  title="Close avatar panel"
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6L6 18"/>
                    <path d="m6 6 12 12"/>
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* === VIDEO PANEL === */}
          {videoOpen && (
            <div 
              className="fixed bg-[#FAFAF6] flex flex-col p-4 pt-6 transition-all duration-300 ease-in-out z-50"
              style={{ top: '8px', right: '8px', width: '320px' }}
            >
              {/* Video Container */}
              <div className="relative w-full h-48 bg-[#FAFAF6] rounded-lg overflow-hidden border border-[#EBECE6]">
                <video
                  ref={videoRef}
                  width="100%"
                  height="100%"
                  autoPlay
                  muted
                  className="w-full h-full object-cover"
                  style={{
                    background: "#FAFAF6",
                    transform: "scaleX(-1)"
                  }}
                />
                {/* Close button overlay */}
                <button
                  onClick={handleStopVideo}
                  className="absolute top-2 right-2 w-6 h-6 bg-black bg-opacity-50 rounded-full flex items-center justify-center text-white hover:bg-opacity-70 transition"
                  title="Close video"
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6L6 18"/>
                    <path d="m6 6 12 12"/>
                  </svg>
                </button>
              </div>
            </div>
          )}

          {agentNotification && (
            <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-200 text-yellow-900 text-center py-2 shadow">
              {agentNotification}
            </div>
          )}

          {/* Image Zoom Modal */}
          {zoomImageUrl && (
            <div
              className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center"
              onClick={closeZoom}
            >
              <div className="relative max-w-full max-h-full p-4">
                <img
                  src={zoomImageUrl}
                  alt="Zoomed"
                  className="rounded-lg max-h-[90vh] max-w-[90vw] object-contain"
                />
                <button
                  onClick={closeZoom}
                  className="absolute top-2 right-2 text-white bg-black bg-opacity-60 hover:bg-opacity-90 p-2 rounded-full"
                  aria-label="Close"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18"/>
                    <path d="m6 6 12 12"/>
                  </svg>
                </button>
              </div>
            </div>
          )}


        </div>
      
    </div>
  );
}

export default ChatWindow;